// O IATA da companhia a partir do nome que a fonte manda (antes em trip-document `format.ts`).
/* As três companhias que a fonte nomeia por extenso, e o código que o CDN de logos
   do genui-kit (`AirlineLogo`) entende. Mesma chave por PRIMEIRA PALAVRA do
   `airlineLogoSvg` do trip-panel (`airline_logos.tsx`), e pela mesma razão: a
   fonte manda a razão social ("LATAM Airlines", "Gol Linhas Aéreas"). */
const IATA_BY_FIRST_WORD: Record<string, string> = { LATAM: 'LA', GOL: 'G3', AZUL: 'AD' };

/* As estrangeiras (e as brasileiras fora das três) pelo nome INTEIRO, já sem o
   sufixo genérico (`carrierKey`): a primeira palavra não serve aqui — "Air France"
   e "Air Canada" dividem o "AIR", "American" e "American Eagle" dividem o
   "AMERICAN". Fora da tabela não há IATA, e quem desenha cai no avião genérico. */
const IATA_BY_NAME: Record<string, string> = {
  'AEROLINEAS ARGENTINAS': 'AR',
  AEROMEXICO: 'AM',
  'AIR CANADA': 'AC',
  'AIR CHINA': 'CA',
  'AIR EUROPA': 'UX',
  'AIR FRANCE': 'AF',
  ALASKA: 'AS',
  'ALL NIPPON': 'NH',
  AMERICAN: 'AA',
  ARAJET: 'DM',
  AVIANCA: 'AV',
  'BOLIVIANA DE AVIACION': 'OB',
  BRITISH: 'BA',
  'CATHAY PACIFIC': 'CX',
  COPA: 'CM',
  DELTA: 'DL',
  EMIRATES: 'EK',
  ETHIOPIAN: 'ET',
  ETIHAD: 'EY',
  FLYBONDI: 'FO',
  IBERIA: 'IB',
  ITA: 'AZ',
  JAPAN: 'JL',
  JETBLUE: 'B6',
  JETSMART: 'JA',
  KLM: 'KL',
  'KLM ROYAL DUTCH': 'KL',
  'KOREAN AIR': 'KE',
  LUFTHANSA: 'LH',
  PARANAIR: 'ZP',
  QATAR: 'QR',
  'ROYAL AIR MAROC': 'AT',
  SINGAPORE: 'SQ',
  SKY: 'H2',
  'SOUTH AFRICAN': 'SA',
  SWISS: 'LX',
  'SWISS INTERNATIONAL': 'LX',
  TAP: 'TP',
  'TAP AIR PORTUGAL': 'TP',
  TURKISH: 'TK',
  UNITED: 'UA',
  VOEPASS: '2Z',
};

/** O nome como chave da tabela: caixa alta, sem acento, sem pontuação e sem o
 *  sufixo que não distingue ninguém ("Airlines", "Air Lines", "Airways", "Linhas
 *  Aéreas", "S.A."). "Delta Air Lines" e "Delta Airlines" viram o mesmo "DELTA". */
function carrierKey(upper: string): string {
  return upper
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .replaceAll(/[^A-Z0-9 ]/g, ' ')
    .replaceAll(/\b(AIR LINES|AIRLINES?|AIRWAYS|LINHAS AEREAS|LINEAS AEREAS|S A|SA|INC)\b/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim();
}

/** O IATA da companhia a partir do que a fonte manda — o código pelado quando ela
 *  já manda código ("AD"), a primeira palavra das três brasileiras, e o nome
 *  inteiro das demais (`IATA_BY_NAME`). `null` quando nenhum dos três: aí quem
 *  desenha mostra o nome ou o avião genérico, não um buraco.
 *
 *  Existe porque as duas famílias de logo do repo pedem entradas diferentes: o
 *  `airlineLogoSvg` do trip-panel casa por NOME (3 SVGs curados), e o
 *  `AirlineLogo` do intelligence-ui-kit serve por IATA (cobertura mundial, via
 *  CDN). A fonte manda a cia estrangeira por extenso (`cia: "United Airlines"`),
 *  então sem o nome→código ela ficava sem logo nenhuma (#5653). */
export function airlineIata(value: string | null | undefined): string | null {
  const raw = value?.trim().toUpperCase();
  if (!raw) return null;
  if (/^[A-Z0-9]{2}$/.test(raw)) return raw;
  return IATA_BY_FIRST_WORD[raw.split(/\s+/)[0] ?? ''] ?? IATA_BY_NAME[carrierKey(raw)] ?? null;
}
